import React, { useRef } from 'react';
import { htmlDecode } from '../helpers/functions';
import { useWindowScroll } from 'react-use';
import { useIsScreenSize } from '../helpers/hooks';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, MemoryRouter, Switch, Route } from 'react-router-dom';
import { SocialMedia } from './SocialMedia';
import cx from 'classnames';
import styles from './Header.module.scss';
import { ReactComponent as Logo } from '../assets/HRT_Logo.svg';
import { ReactComponent as Chevron } from '../assets/chevron-right.svg';
import { ReactComponent as Phone } from '../assets/phone.svg';
import { ReactComponent as Mail } from '../assets/mail.svg';
import { ReactComponent as Location } from '../assets/location.svg';

const GlobalNavContext = React.createContext({});

const icons = {
  phone: Phone,
  mail: Mail,
  location: Location,
};

export function Header({
  siteUrl,
  siteName,
  socialLinks,
  siteLogo,
  mainNav,
  topLeft,
  topRight,
}) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [stickyTopValue, setStickyTopValue] = React.useState(0);
  const topLeftBlocks = JSON.parse(htmlDecode(topLeft));
  const topRightBlocks = JSON.parse(htmlDecode(topRight));

  const ref = useRef();
  const stickyRef = useRef();

  const isMobile = useIsScreenSize('xl');

  const { x, y } = useWindowScroll();

  const containerRef = useRef(null);

  const sticky = y > 400;

  function handleBurgerClick() {
    document.body.classList.toggle('overflow-hidden');
    if (sticky) {
      setStickyTopValue(`${stickyRef.current.clientHeight}px`);
    } else {
      setStickyTopValue(`${ref.current.clientHeight}px`);
    }

    setIsMenuOpen((val) => !val);
  }

  const value = {
    siteUrl,
    siteName,
    socialLinks,
    siteLogo,
    mainNav,
    topLeftBlocks,
    topRightBlocks,
    isMenuOpen,
    stickyTopValue,
    handleBurgerClick,
    sticky,
  };

  return (
    <GlobalNavContext.Provider value={value}>
      <nav ref={ref} id="header" ref={ref}>
        {isMobile ? <MobileHeader /> : <DesktopHeader />}
      </nav>
      <AnimatePresence>{isMenuOpen && <MobileNavSlideout />}</AnimatePresence>
      {sticky && (
        <div
          className="fixed top-0 right-0 left-0 bg-primary transition duration-200 z-60"
          ref={stickyRef}
        >
          <StickyNav />
        </div>
      )}
      {sticky && (
        <div
          className="fixed cursor-pointer bottom-8 right-8 p-2 bg-secondary opacity-80 transform -rotate-90 z-80 hover:opacity-100 transition duration-200"
          onClick={() => {
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
          }}
        >
          <Chevron className="w-4 h-4 text-white" />
        </div>
      )}
    </GlobalNavContext.Provider>
  );
}

function SiteLogo({ className, width = `338px` }) {
  const { siteLogo, siteName, siteUrl } = React.useContext(GlobalNavContext);
  return (
    <a
      href={siteUrl}
      className={cx(className, `block`)}
      style={{ maxWidth: width }}
    >
      {siteLogo ? (
        <img src={siteLogo} alt={siteName} />
      ) : (
        <Logo className="w-full block fill-current text-primary mx-auto" />
      )}
    </a>
  );
}

function DesktopHeader() {
  const { topLeftBlocks, topRightBlocks, mainNav, socialLinks } =
    React.useContext(GlobalNavContext);
  return (
    <div className="relative z-50">
      <div className="bg-secondary">
        <div className="container flex items-center py-2">
          <div className="w-1/2">
            {topLeftBlocks &&
              topLeftBlocks.map((link, key) => {
                const Icon = icons[link.icon];
                return (
                  <a
                    href={link.link}
                    key={key}
                    className="text-white flex items-center space-x-2 no-underline group hover:text-primary"
                  >
                    <Icon className="w-8 h-6 fill-current text-white group-hover:text-primary" />
                    <span dangerouslySetInnerHTML={{ __html: link.text }} />
                  </a>
                );
              })}
          </div>
          <div className="w-1/2 flex justify-end items-center space-x-4">
            {topRightBlocks &&
              topRightBlocks.map((link, key) => (
                <a
                  href={link.link}
                  className="inline-block px-4 py-2 transition text-sm hover:bg-primary text-white border border-primary rounded-full no-underline font-bold"
                  dangerouslySetInnerHTML={{ __html: link.text }}
                  key={key}
                />
              ))}
            {socialLinks && (
              <SocialMedia
                links={socialLinks}
                color={`text-white`}
                hover={`hover:text-primary`}
              />
            )}
          </div>
        </div>
      </div>
      <div className="bg-primary py-4">
        <div className="container flex items-center">
          <div className="w-3/12">
            <SiteLogo />
          </div>
          <div className="w-9/12">{mainNav && <DesktopNavigation />}</div>
        </div>
      </div>
    </div>
  );
}

function DesktopNavigation() {
  const { siteLogo, mainNav, sticky } = React.useContext(GlobalNavContext);
  const [activeMenuItem, setActiveMenuItem] = React.useState(null);

  return (
    <nav className="flex container justify-end bg-primary">
      {mainNav.map((item, key) => (
        <div
          className="group relative"
          key={key}
          onMouseEnter={() => {
            if (item.hasOwnProperty('children')) {
              setActiveMenuItem(key);
            } else {
              setActiveMenuItem(null);
            }
          }}
          onMouseLeave={() => {
            setActiveMenuItem(null);
          }}
        >
          <a
            href={item.url !== '#' ? item.url : ''}
            className={cx(
              `flex space-x-2 items-center px-4 group-hover:text-secondary no-underline font-bold font-secondary uppercase text-white text-sm`,
              sticky ? `py-1` : `py-2`
            )}
          >
            <span
              dangerouslySetInnerHTML={{ __html: htmlDecode(item.title) }}
            />
            {item.hasOwnProperty('children') && (
              <Chevron className="w-4 h-4 transform rotate-90 group-hover:text-secondary fill-current" />
            )}
          </a>
          <AnimatePresence>
            {activeMenuItem === key && (
              <motion.div
                className={cx(
                  `absolute drop-shadow-2xl top-full left-1/2 bg-white text-center font-secondary`
                )}
                initial={{ y: 50, opacity: 0, x: '-50%' }}
                animate={{ y: 0, opacity: 1, x: '-50%' }}
                exit={{ y: 50, opacity: 0, x: '-50%' }}
                transition={{ duration: 0.1 }}
                style={{ width: '230px' }}
              >
                {item.children.map((child, key2) => (
                  <a
                    href={child.url}
                    key={key2}
                    dangerouslySetInnerHTML={{
                      __html: htmlDecode(child.title),
                    }}
                    className="relative z-20 block px-2 py-3 no-underline font-primary text-18 hover:text-white hover:bg-secondary text-secondary"
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </nav>
  );
}

function MobileHeader() {
  return (
    <div className="relative">
      <div className="flex items-center px-4 py-4 z-50 bg-primary">
        <div className="w-1/2">
          <SiteLogo className="max-w-12" />
        </div>
        <div className="w-1/2 flex justify-end">
          <Burger className="relative" />
        </div>
      </div>
    </div>
  );
}

function StickyNav() {
  return (
    <div className="relative">
      <div className="flex container items-center px-4 py-2 z-50">
        <div className="w-1/2">
          <SiteLogo className="" width={`150px`} />
        </div>
        <div className="w-1/2 flex justify-end">
          <Burger className="relative" />
        </div>
      </div>
    </div>
  );
}

function MobileNavSlideout() {
  const {
    mainNav,
    handleBurgerClick,
    topLeftBlocks,
    stickyTopValue,
    topRightBlocks,
  } = React.useContext(GlobalNavContext);
  return (
    <motion.div
      className={cx(
        `fixed z-70 top-0 right-0 bottom-0 bg-primary`,
        styles.mobile_slideout
      )}
      initial={{ x: 375, y: stickyTopValue }}
      animate={{ x: 0, y: stickyTopValue }}
      exit={{ x: 375, y: stickyTopValue }}
      transition={{ duration: 0.25 }}
    >
      <MemoryRouter initialEntries={['/']} initialIndex={0}>
        <Switch>
          <Route path="/" exact>
            {mainNav.map((item, key) => {
              if (item.children) {
                return (
                  <Link
                    key={key}
                    to={item.url}
                    className="block bg-primary text-white hover:bg-secondary p-4 font-bold no-underline flex justify-between items-center border-b border-white"
                  >
                    <span
                      className="text-white mr-4"
                      dangerouslySetInnerHTML={{
                        __html: htmlDecode(item.title),
                      }}
                    />
                    <Chevron className="w-6 h-6" />
                  </Link>
                );
              } else {
                return (
                  <a
                    key={key}
                    href={item.url}
                    dangerouslySetInnerHTML={{
                      __html: htmlDecode(item.title),
                    }}
                    className="block bg-primary text-white hover:bg-secondary p-4 font-bold no-underline border-b border-white"
                  />
                );
              }
            })}
          </Route>
          {mainNav.map((item, key) => {
            if (item.children) {
              return (
                <Route path={`/${item.url}`} exact key={key}>
                  <a
                    href={item.url}
                    dangerouslySetInnerHTML={{
                      __html: 'View ' + htmlDecode(item.title),
                    }}
                    className="block bg-primary text-white  p-4 font-bold no-underline border-b border-white last:border-none"
                  />
                  {item.children.map((child, key2) => (
                    <a
                      key={key2}
                      href={child.url}
                      dangerouslySetInnerHTML={{
                        __html: htmlDecode(child.title),
                      }}
                      className="block bg-primary text-white  p-4 font-bold no-underline border-b border-white last:border-none"
                    />
                  ))}
                  <Link
                    to="/"
                    className="block bg-white text-primary p-4 font-bold no-underline flex items-center"
                  >
                    <Chevron className="w-12 h-6 pr-4 fill-current text-primary transform rotate-180" />
                    <span>Back</span>
                  </Link>
                </Route>
              );
            }
          })}
        </Switch>
      </MemoryRouter>
    </motion.div>
  );
}

const Burger = ({ className, ...props }) => {
  const { isMenuOpen, isSticky, handleBurgerClick } =
    React.useContext(GlobalNavContext);
  const height = '3px';
  const width = '22px';
  return (
    <motion.div
      className={cx(`block group cursor-pointer`, className)}
      style={{ width: '35px', height: '35px' }}
      whileTap={{ scale: 0.8 }}
      onClick={handleBurgerClick}
      {...props}
    >
      <div
        className={cx(
          `bg-white group-hover:bg-white transition duration-150 ease-out absolute left-0 right-0 mx-auto`,
          isMenuOpen ? `transform rotate-45` : ``
        )}
        style={{
          height: height,
          width: width,
          top: isMenuOpen ? `16px` : `9px`,
        }}
      ></div>
      <div
        className={cx(
          `bg-white group-hover:bg-white transition mx-auto duration-150 ease-out absolute left-0 right-0`,
          isMenuOpen && `opacity-0`
        )}
        style={{ height: height, width: width, bottom: '15px' }}
      ></div>
      <div
        className={cx(
          `bg-white group-hover:bg-white transition mx-auto duration-150 ease-out absolute left-0 right-0`,
          isMenuOpen && `transform -rotate-45`
        )}
        style={{
          height: height,
          width: width,
          bottom: isMenuOpen ? '15px' : '8px',
        }}
      ></div>
    </motion.div>
  );
};
